import axios from 'axios';
import { reportApiError } from './reportClientError';
import toast from 'react-hot-toast';
import i18n from '../i18n';

// Every localStorage/sessionStorage key that carries a credential or session.
// Everything ELSE in localStorage is UI state (theme, lang, nav_order,
// per-module tab/view prefs, notification seen-ids, cmdk_recents_v1__<user>,
// cmdk_intro_v4_seen…) and must survive a logout.
// `token` is a legacy key: a few pages still read it, nothing writes it — kept
// here so an old value from a previous app version can't linger.
const AUTH_LOCAL_KEYS = ['accessToken', 'refreshToken', 'token'];
const AUTH_SESSION_KEYS = ['vaultToken', 'admin_accessToken', 'admin_refreshToken'];

// Remove only auth tokens — preserves UI prefs (theme, nav_order, notif_sound, etc.)
export function clearAuthTokens() {
  AUTH_LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
}

// Full session teardown for a hard, unrecoverable 401. Drops every auth key in
// BOTH storages — but NOT with localStorage.clear().
// Why the blanket clear was wrong: it also wiped harmless per-user UI state,
// so an expired session silently reset the palette's "Recientes" list, re-fired
// already-dismissed intros/spotlights, and reset every saved tab/view pref. A
// session expiring is not a request to forget the user's preferences.
export function clearSession() {
  clearAuthTokens();
  AUTH_SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k));
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

// Attach access token to every request
// Renovación deslizante de la sesión del Vault. El token dura 30 min, pero el
// bloqueo tiene que ser por INACTIVIDAD (como 1Password/Bitwarden): si estás
// trabajando no te puede cortar en medio. Cada vez que se usa el Vault renovamos
// en segundo plano, como mucho una vez cada 5 min. El backend impone el tope
// absoluto de 12 h desde que se puso el PIN, así que esto no extiende para siempre.
const VAULT_RENEW_EVERY_MS = 5 * 60 * 1000;
let lastVaultRenew = 0;
let renewInFlight = false;

function maybeRenewVaultSession() {
  const now = Date.now();
  if (renewInFlight || now - lastVaultRenew < VAULT_RENEW_EVERY_MS) return;
  if (!sessionStorage.getItem('vaultToken')) return;
  renewInFlight = true;
  lastVaultRenew = now;
  // Cliente axios crudo: evita re-entrar en este mismo interceptor.
  axios.post(`${process.env.REACT_APP_API_URL || '/api'}/vault/pin/renew`, {}, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      'X-Vault-Token': sessionStorage.getItem('vaultToken'),
    },
  })
    .then((r) => { if (r.data?.vaultToken) sessionStorage.setItem('vaultToken', r.data.vaultToken); })
    .catch(() => { /* expiró o superó el tope: el próximo 401 re-pide el PIN */ })
    .finally(() => { renewInFlight = false; });
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Attach vault session token for vault and HR routes (PIN-protected)
  if (config.url && (config.url.includes('/vault') || config.url.includes('/hr'))) {
    const vaultToken = sessionStorage.getItem('vaultToken');
    if (vaultToken) config.headers['X-Vault-Token'] = vaultToken;
    // No renovar cuando la llamada ES la renovación (evita recursión).
    if (vaultToken && !config.url.includes('/pin/renew')) maybeRenewVaultSession();
  }
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  refreshQueue = [];
};

// Determine the correct login page based on current URL
const getLoginPath = () => window.location.pathname.startsWith('/portal') ? '/portal/login' : '/login';

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // La telemetría no puede tener PODER SOBRE LA SESIÓN. Sale de un timer de
    // fondo cada 15s, así que puede dispararse cuando no hay token (después de
    // un logout, en la pantalla de login, en una pestaña vieja) — y un 401 sin
    // `code` cae en el 401 genérico de abajo, que hace clearSession() + redirect
    // al login. Resultado: un ping de métricas echaba al usuario de la sesión.
    // Se corta acá arriba: un fallo de telemetría se descarta y nada más.
    if (original?._telemetry) return Promise.reject(error);

    // Durante un deploy el servidor se reinicia y deja de contestar ~45 segundos.
    // Una lectura que cae en esa ventana mostraba la pantalla VACÍA ("Todavía no hay
    // ideas", "No hay repuestos") como si los datos hubieran desaparecido — el 15-oct-2026
    // hubo tres deploys y Thiago lo reportó en cinco módulos distintos. Las LECTURAS se
    // reintentan solas hasta ~1 minuto: el deploy se nota como una demora, no como datos
    // perdidos. Las escrituras NO se reintentan (crear un ticket dos veces es peor que un
    // cartel de error). Las cámaras quedan afuera: su 502 es "cámara apagada", no deploy.
    {
      const status = error.response?.status;
      const caido = !error.response || [502, 503, 504, 521, 522, 523].includes(status);
      const metodo = (original?.method || 'get').toLowerCase();
      const url = original?.url || '';
      if (original && caido && (metodo === 'get' || metodo === 'head') && !axios.isCancel?.(error)
          && !/\/(cctv|monitoring)\/.*\/snapshot/.test(url) && !original._noDeployRetry) {
        const PAUSAS = [2000, 3000, 5000, 8000, 10000, 12000, 15000];
        original._deployRetry = (original._deployRetry || 0) + 1;
        if (original._deployRetry <= PAUSAS.length) {
          await new Promise(r => setTimeout(r, PAUSAS[original._deployRetry - 1]));
          return api(original);
        }
      }
      // Una escritura que cae en el reinicio: el cartel de la pantalla dice "no se pudo
      // crear/guardar" y nada más (Thiago intentó crear un ticket 3 veces). Se suma un
      // aviso con el motivo real y qué hacer. id fijo → uno solo aunque fallen varias.
      if (original && caido && metodo !== 'get' && metodo !== 'head' && !original._telemetry
          && !/\/(cctv|monitoring)\/.*\/snapshot/.test(url)) {
        try { toast(i18n.t('common.serverRestarting'), { id: 'server-restarting', icon: '⏳', duration: 8000 }); } catch { /* nada */ }
      }
    }

    // Un 5xx significa que algo del servidor se rompió y el usuario está viendo un
    // cartel de error. Hasta ahora eso no dejaba ningún rastro nuestro: la Cola de
    // revisión devolvió 500 durante semanas y nos enteramos porque un pasante sacó
    // capturas del F12. Se reporta acá, en el único lugar por donde pasan TODAS las
    // llamadas, y sin tocar el flujo: el error sigue su camino igual.
    try { reportApiError(error); } catch { /* reportar nunca puede romper */ }

    // Impersonation read-only block
    if (error.response?.status === 403 && error.response.data?.code === 'IMPERSONATION_READ_ONLY') {
      window.dispatchEvent(new CustomEvent('impersonation-blocked'));
      return Promise.reject(error);
    }

    // Un 401 de las puertas de ENTRADA (contraseña, 2FA, biometría, Google) no es
    // "se venció tu sesión": es "esa credencial no sirve", y la pantalla de login
    // tiene que mostrar el motivo. Antes caía en el 401 genérico de abajo, que
    // hace clearSession() + window.location.href = '/login' → la página se
    // recargaba y el cartel rojo desaparecía a los milisegundos. Así el login
    // biométrico "rebotaba" sin explicación (WEBAUTHN_VAULT_ONLY nunca se leyó).
    const PRE_SESSION_AUTH = /\/auth\/(login|google|webauthn\/login-(options|verify))(\?|$)/;
    if (error.response?.status === 401 && PRE_SESSION_AUTH.test(original?.url || '')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry) {
      // Vault PIN required/expired, or a wrong PIN entered — not a real auth
      // failure, just pass through. The page-level handler re-shows the PIN
      // screen or toasts "invalid PIN". A wrong PIN must NOT clear the JWT and
      // log the user out of the whole app (that caused a PIN→login→menu loop).
      if (error.response.data?.code === 'VAULT_REQUIRED' || error.response.data?.code === 'VAULT_EXPIRED' || error.response.data?.code === 'PIN_INVALID') {
        return Promise.reject(error);
      }

      // Force-logged out by admin — kick immediately
      if (error.response.data?.code === 'FORCE_LOGOUT') {
        clearAuthTokens();
        window.location.href = getLoginPath();
        return Promise.reject(error);
      }

      if (error.response.data?.code === 'TOKEN_EXPIRED') {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          }).then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          });
        }

        original._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          // No refresh token — if we have stashed admin tokens, this is an expired impersonation
          if (sessionStorage.getItem('admin_accessToken')) {
            window.dispatchEvent(new CustomEvent('impersonation-expired'));
            return Promise.reject(error);
          }
          clearAuthTokens();
          window.location.href = getLoginPath();
          return Promise.reject(error);
        }

        // Cross-tab guard: if another tab already rotated the token, reuse it
        // instead of firing a redundant refresh that would fail (token rotation
        // deletes the old refresh token in the DB).
        const sentAccess = original.headers.Authorization?.replace('Bearer ', '');
        const currentAccess = localStorage.getItem('accessToken');
        if (currentAccess && currentAccess !== sentAccess) {
          processQueue(null, currentAccess);
          original.headers.Authorization = `Bearer ${currentAccess}`;
          isRefreshing = false;
          return api(original);
        }

        try {
          const res = await axios.post('/api/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefresh } = res.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefresh);
          api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          processQueue(null, accessToken);
          original.headers.Authorization = `Bearer ${accessToken}`;
          return api(original);
        } catch (err) {
          // Another tab may have won the rotation race in the meantime — recheck storage
          const newerAccess = localStorage.getItem('accessToken');
          const newerRefresh = localStorage.getItem('refreshToken');
          if (newerAccess && newerRefresh && newerRefresh !== refreshToken) {
            api.defaults.headers.common.Authorization = `Bearer ${newerAccess}`;
            processQueue(null, newerAccess);
            original.headers.Authorization = `Bearer ${newerAccess}`;
            return api(original);
          }
          processQueue(err, null);
          clearAuthTokens();
          window.location.href = getLoginPath();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      // Generic 401 — if impersonating, auto-restore
      if (sessionStorage.getItem('admin_accessToken')) {
        window.dispatchEvent(new CustomEvent('impersonation-expired'));
        return Promise.reject(error);
      }
      clearSession();
      window.location.href = getLoginPath();
    }

    // Friction signal. Every rollup we have counts what agents DO; nothing
    // counts what fails on them, so a module that is heavily used AND breaks
    // often is indistinguishable from one that just works. Emitted as a DOM
    // event rather than a direct call so api.js and utils/trackAction.js don't
    // import each other — trackAction.js posts through this very instance, and
    // a cycle there would be resolved at module-init time, before the
    // interceptor exists.
    //
    // Deliberately fires AFTER every handled case above: a 401 that the refresh
    // flow retried successfully is normal token rotation, not friction, and
    // counting it would bury the real failures under thousands of rows.
    try {
      // Una cámara apagada NO es fricción de la interfaz. El proxy de snapshot
      // devuelve 502 cuando la cámara no contesta o cuando su dirección pertenece
      // al túnel de otro cliente (CrossClientAddressError), y en los dos casos el
      // sistema está haciendo lo correcto. Un widget que refresca cámaras cada
      // pocos segundos convierte eso en miles de eventos: 8.278 en 30 días, todos
      // de un mismo cliente, tapando el panel de fricción que existe justamente
      // para encontrar lo que SÍ está roto. Se excluye por ruta, no por cliente,
      // así vale para cualquier cámara offline de cualquiera.
      const url = error.config?.url || '';
      const esSnapshotDeCamara = /\/(cctv|monitoring)\/.*\/snapshot/.test(url);
      if (!axios.isCancel?.(error) && !esSnapshotDeCamara) {
        window.dispatchEvent(new CustomEvent('api-error', {
          detail: {
            status: error.response?.status || 0, // 0 = no response (network/CORS/timeout)
            method: (error.config?.method || '').toLowerCase(),
          },
        }));
      }
    } catch { /* telemetry must never mask the original error */ }

    return Promise.reject(error);
  }
);

// Formats an Argentine mobile number to +54 9 XX XXXX-XXXX
// Works with any input format (with/without spaces, dashes, country code)
export function formatWhatsApp(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // 13 digits: 54 + 9 + 2-digit area + 8-digit local  e.g. 5491157587882
  if (digits.length === 13 && digits.startsWith('54')) {
    const area = digits.slice(3, 5);
    const local = digits.slice(5);
    return `+54 9 ${area} ${local.slice(0, 4)}-${local.slice(4)}`;
  }
  // 11 digits: 9 + 2-digit area + 8-digit local (no country code)
  if (digits.length === 11 && digits.startsWith('9')) {
    const area = digits.slice(1, 3);
    const local = digits.slice(3);
    return `+54 9 ${area} ${local.slice(0, 4)}-${local.slice(4)}`;
  }
  return raw;
}

export default api;
